import { Input } from "@/shared/components/ui/input";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export type IntNumberInputProps = {
  id?: string;
  value: number;
  onChange?: (value: number) => void;
  onBlur?: (value: number) => void;
  className?: string;
  disabled?: boolean;
  min?: number;
};

export function IntNumberInput({
  id,
  value,
  onChange,
  onBlur: onBlurProp,
  className = "",
  disabled = false,
  min = 0,
}: IntNumberInputProps) {
  const [intValue, setIntValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const focused = useRef(false);

  useEffect(() => {
    if (focused.current) {
      return;
    }
    setIntValue(value); // Sync internal state with the value prop
  }, [value]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey && !disabled) {
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setIntValue((prev) => {
            const newValue = prev + 1;
            if (onChange) {
              onChange(newValue);
            }
            return newValue;
          });
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          setIntValue((prev) => {
            const newValue = prev - 1 < 0 ? 0 : prev - 1;
            if (onChange) {
              onChange(newValue);
            }
            return newValue;
          });
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          setIntValue((prev) => {
            const newValue = prev - 10 < 0 ? 0 : prev - 10;
            if (onChange) {
              onChange(newValue);
            }
            return newValue;
          });
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          setIntValue((prev) => {
            const newValue = prev + 10;
            if (onChange) {
              onChange(newValue);
            }
            return newValue;
          });
        } else if (e.key === "0") {
          e.preventDefault();
          setIntValue(() => {
            const newValue = 0;
            if (onChange) {
              onChange(newValue);
            }
            return newValue;
          });
        }
      }
    };

    const inputElement = inputRef.current;
    inputElement?.addEventListener("keydown", handleKeyDown);

    return () => {
      inputElement?.removeEventListener("keydown", handleKeyDown);
    };
  }, [onChange, disabled]);

  const updateValue = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const parsedValue = rawValue === "" ? 0 : parseInt(rawValue, 10);

    if (!Number.isNaN(parsedValue)) {
      if (intValue !== parsedValue) {
        setIntValue(parsedValue);
        if (onChange) {
          onChange(parsedValue);
        }
      }
    }
  };

  const handleBlur = () => {
    focused.current = false;
    const raw =
      inputRef.current?.value ??
      (intValue !== undefined && intValue !== null ? intValue.toString() : "");
    const parsed = raw === "" ? 0 : parseInt(raw, 10);
    if (!Number.isNaN(parsed)) {
      const finalValue = Math.max(min, parsed);
      setIntValue(finalValue);
      if (onChange) {
        onChange(finalValue);
      }
      if (onBlurProp) {
        onBlurProp(finalValue);
      }
    } else {
      // reset to prop value if parsing failed
      setIntValue(value);
    }
  };

  return (
    <Input
      id={id}
      ref={inputRef}
      type="text"
      autoComplete="off"
      inputMode="numeric"
      pattern="[0-9]*"
      min={min}
      step="1"
      className={`h-8 w-24 text-end ${className}`}
      value={
        intValue !== undefined && intValue !== null ? intValue.toString() : ""
      }
      disabled={disabled}
      onChange={updateValue}
      onFocus={() => {
        focused.current = true;
      }}
      onBlur={handleBlur}
    />
  );
}

export type FloatNumberInputProps = {
  id?: string;
  value: number;
  onChange?: (value: number) => void;
  handleEnterKeyDown?: (value: number) => void;
  onBlur?: (value: number) => void;
  className?: string;
  fractions?: number;
  disabled?: boolean;
  locale?: string; // Optional locale, defaults to 'de-DE' for ',' as decimal separator
};

export function FloatNumberInput({
  id,
  value,
  onChange,
  handleEnterKeyDown,
  onBlur,
  className = "",
  fractions = 2,
  disabled = false,
  locale = "de-DE",
}: FloatNumberInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: fractions,
        maximumFractionDigits: fractions,
      }),
    [locale, fractions],
  );

  const formatLocaleNumber = useCallback(
    (number: number) => formatter.format(Number.isNaN(number) ? 0 : number),
    [formatter],
  );
  const [inputValue, setInputValue] = useState(formatLocaleNumber(value));

  const parseLocaleNumber = useCallback(
    (input: string) => {
      const decimalSeparator = formatter.format(1.1).charAt(1);
      const groupSeparator = formatter.format(1111).charAt(1);
      const normalized = input
        .replaceAll(groupSeparator, "")
        .replaceAll(decimalSeparator, ".")
        .replaceAll(",", ".")
        .trim();
      return parseFloat(normalized);
    },
    [formatter],
  );

  const updateValue = useCallback(
    (increment: number) => {
      const parsedValue = parseLocaleNumber(inputValue);
      if (!Number.isNaN(parsedValue)) {
        const newValue = Math.max(0, parsedValue + increment);
        const roundedValue = parseFloat(newValue.toFixed(fractions));
        setInputValue(formatLocaleNumber(roundedValue));
        if (onChange) {
          onChange(roundedValue);
        }
      }
    },
    [formatLocaleNumber, fractions, inputValue, onChange, parseLocaleNumber],
  );

  useEffect(() => {
    if (!isFocused) {
      setInputValue(formatLocaleNumber(value));
    }
  }, [value, locale, formatLocaleNumber, isFocused]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!disabled) {
        if (e.key === "Enter" && handleEnterKeyDown) {
          e.preventDefault();
          const parsedValue = parseLocaleNumber(inputValue);
          if (!Number.isNaN(parsedValue)) {
            handleEnterKeyDown(parsedValue);
          }
        } else if (e.shiftKey) {
          if (e.key === "ArrowUp") {
            e.preventDefault();
            updateValue(1);
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            updateValue(-1);
          }
        }
      }
    };

    const inputElement = inputRef.current;
    inputElement?.addEventListener("keydown", handleKeyDown);

    return () => {
      inputElement?.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    inputValue,
    disabled,
    handleEnterKeyDown,
    parseLocaleNumber,
    updateValue,
  ]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    if (rawValue === "") {
      setInputValue("");
      if (onChange) {
        onChange(0);
      }
      return;
    }

    setInputValue(rawValue);

    const parsedValue = parseLocaleNumber(rawValue);
    if (!Number.isNaN(parsedValue)) {
      const roundedValue = parseFloat(parsedValue.toFixed(fractions));
      if (onChange) {
        onChange(roundedValue);
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsedValue = parseLocaleNumber(inputValue);
    if (!Number.isNaN(parsedValue)) {
      const roundedValue = parseFloat(parsedValue.toFixed(fractions));
      const formattedValue = formatLocaleNumber(roundedValue);
      setInputValue(formattedValue);
      if (onChange) {
        onChange(roundedValue);
      }
      if (onBlur) {
        onBlur(roundedValue);
      }
    } else {
      setInputValue(formatLocaleNumber(value));
    }
  };

  return (
    <Input
      id={id}
      ref={inputRef}
      type="text"
      autoComplete="off"
      inputMode="decimal"
      pattern="[0-9]*[.,]?[0-9]*"
      className={`h-8 w-24 text-end ${className}`}
      value={inputValue}
      disabled={disabled}
      onFocus={() => setIsFocused(true)}
      onChange={handleInputChange}
      onBlur={handleBlur}
    />
  );
}

export function round(value: number, decimals: number = 2): number {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

export function Value({
  value,
  fractions = 2,
  id,
  className,
}: {
  value: number;
  fractions?: number;
  id?: string;
  className?: string;
}) {
  return (
    <span id={id} className={`border-b-2 border-white text-right ${className}`}>
      {value.toLocaleString("de-DE", {
        minimumFractionDigits: fractions ?? 0,
        maximumFractionDigits: fractions ?? 0,
      })}
    </span>
  );
}

export function fHours(hours: number): string {
  return hours.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function fMoney(money: number): string {
  return money.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
